import React from 'react';
import { RowActions, type RowAction } from '@/components/ui';
import type { PcrSubmission } from '../../../services/pcrService';

export interface PcrRowActionsProps {
  row: PcrSubmission;
  pcrReplyUiEnabled?: boolean;
  canPcrReply?: boolean;
  canUpdateStatus?: boolean;
  /** Move New → Internal Review (pcr:internal_review or submission:pcr_update | workId:create). */
  canMoveToInternalReview?: boolean;
  onMoveToInternalReview?: (row: PcrSubmission) => void;
  /** Move Internal Review → Finalized (pcr:finalize). */
  canMoveToFinalize?: boolean;
  onMoveToFinalize?: (row: PcrSubmission) => void;
  statusTransitioningId?: string | null;
  onReply?: (row: PcrSubmission) => void;
  onEdit?: (row: PcrSubmission) => void;
  onOpenDetail: (row: PcrSubmission) => void;
  canOpenNotesHistory?: boolean;
  onOpenNotesHistory?: (row: PcrSubmission) => void;
  /** Manual PCR → Work ID (after field assessment). */
  canPromoteToWorkId?: boolean;
  onPromoteToWorkId?: (row: PcrSubmission) => void;
  /** Finalize PCR with no maintenance work. */
  canCloseWithoutWork?: boolean;
  onCloseWithoutWork?: (row: PcrSubmission) => void;
  /** Open Work IDs list filtered to the linked id. */
  onOpenLinkedWorkId?: (row: PcrSubmission) => void;
  /** Full audit PDF (history, emails, Work ID, closure) — same permission gate as Notes & history. */
  canExportAuditReport?: boolean;
  onExportAuditReport?: (row: PcrSubmission) => void;
  auditReportExportingId?: number | null;
}

/**
 * PCR row menu on the shared RowActions kebab (W-L8): the same icon treatment as every other
 * row menu, and actions that cannot be used are hidden rather than shown disabled - the reply
 * action is not offered while PCR email reply is switched off (VITE_PCR_REPLY_UI_ENABLED /
 * pcrReplyUiEnabled) or the user lacks submission:pcr_reply. A reason that matters (no email on
 * file, an export in progress) goes in the label, since a menu item has no hover target.
 */
const PcrRowActions: React.FC<PcrRowActionsProps> = ({
  row,
  pcrReplyUiEnabled,
  canPcrReply,
  canMoveToInternalReview,
  onMoveToInternalReview,
  canMoveToFinalize,
  onMoveToFinalize,
  statusTransitioningId,
  onReply,
  onEdit,
  onOpenDetail,
  canOpenNotesHistory,
  onOpenNotesHistory,
  canPromoteToWorkId,
  onPromoteToWorkId,
  canCloseWithoutWork,
  onCloseWithoutWork,
  onOpenLinkedWorkId,
  canExportAuditReport,
  onExportAuditReport,
  auditReportExportingId,
}) => {
  const hasEmail = Boolean((row.user_email || '').trim());
  const status = row.status || 'new';
  const transitioning = statusTransitioningId != null && statusTransitioningId === String(row.id);
  const showMoveToInternalReview =
    Boolean(canMoveToInternalReview && onMoveToInternalReview) && status === 'new';
  const showMoveToFinalize =
    Boolean(canMoveToFinalize && onMoveToFinalize) && status === 'internal_review' && !row.workIdId;
  const settled = Boolean(row.workIdId) || status === 'finalized';
  const exporting = auditReportExportingId != null && auditReportExportingId === Number(row.id);

  const actions: RowAction[] = [
    { kind: 'view', label: 'View Details', onClick: () => onOpenDetail(row) },
    { kind: 'edit', label: 'Edit PCR', onClick: () => onEdit?.(row), hidden: !onEdit },
    {
      kind: 'custom',
      icon: 'bi-arrow-right-circle',
      label: transitioning ? 'Moving to Internal Review…' : 'Move to Internal Review',
      onClick: () => onMoveToInternalReview?.(row),
      disabled: transitioning,
      hidden: !showMoveToInternalReview,
    },
    {
      kind: 'custom',
      icon: 'bi-check2-circle',
      label: transitioning ? 'Moving to Finalized…' : 'Move to Finalized',
      onClick: () => onMoveToFinalize?.(row),
      disabled: transitioning,
      hidden: !showMoveToFinalize,
    },
    {
      kind: 'history',
      label: 'Notes & History',
      onClick: () => onOpenNotesHistory?.(row),
      hidden: !(canOpenNotesHistory && onOpenNotesHistory),
    },
    {
      kind: 'custom',
      icon: 'bi-file-earmark-pdf',
      label: exporting ? 'Exporting…' : 'Export to PDF',
      onClick: () => onExportAuditReport?.(row),
      disabled: exporting,
      hidden: !(canExportAuditReport && onExportAuditReport),
    },
    {
      kind: 'custom',
      icon: 'bi-envelope',
      label: hasEmail ? 'Send Response to Requester' : 'Send Response to Requester – no email on file',
      onClick: () => onReply?.(row),
      disabled: !hasEmail,
      hidden: !(onReply && canPcrReply && pcrReplyUiEnabled),
    },
    {
      kind: 'custom',
      icon: 'bi-box-arrow-up-right',
      label: 'Open Linked Work ID',
      onClick: () => onOpenLinkedWorkId?.(row),
      hidden: !(row.workIdId && onOpenLinkedWorkId),
    },
    {
      kind: 'custom',
      icon: 'bi-plus-circle',
      label: 'Convert to Work ID…',
      onClick: () => onPromoteToWorkId?.(row),
      hidden: !(canPromoteToWorkId && onPromoteToWorkId) || settled,
    },
    {
      kind: 'custom',
      icon: 'bi-x-circle',
      label: 'Close Without Work ID…',
      onClick: () => onCloseWithoutWork?.(row),
      hidden: !(canCloseWithoutWork && onCloseWithoutWork) || settled,
    },
  ];

  return <RowActions actions={actions} />;
};

export default PcrRowActions;
